import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View, type ViewOpts } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import { createEmitter } from 'src/lib/Emitter';
import uiConfig from 'src/lib/ui/config';
import { duration } from 'src/replicant/utils/duration';
import {
  dreamDriftGetInstanceRemainingTime,
  dreamDriftIsInstanceActive,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import StateObserver, { now, user } from 'src/StateObserver';
import type { PetEntity } from 'src/game/logic/EntityManager';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import { dreamDriftOpen } from '../dreamDrift.sequence';
import { dreamDriftTheme } from '../dreamDrift.theme';

/** Options controlling the active home-pet composition and owner callback. */
type DreamDriftHomePetViewOpts = ViewOpts & {
  onActiveChanged: (isActive: boolean) => void;
};

/** Floating sleeping pet, themed bed, and live Dream Meter presentation. */
export class DreamDriftHomePetView extends View {
  /** Bed view refreshed when a later event reuses this persistent home component. */
  private readonly bed: ImageScaleView;

  /** Live Dream Meter displayed beneath the sleeping pet. */
  private readonly timer: Match2Timer;

  /** Owner callback notified when the active presentation changes. */
  private readonly onActiveChanged: (isActive: boolean) => void;

  /** Full-screen dim layer displayed behind the sleeping-pet composition. */
  private readonly background: View;

  /** Complete visual composition moved independently from the scene anchor. */
  private readonly container: View;

  /** Lazily created pet entity displaying the current species asleep. */
  private sleepingPet?: PetEntity;

  /** Species currently rendered by the sleeping pet entity. */
  private sleepingPetSpecies?: ReturnType<typeof getPet>['species'];

  /** Whether the active Dream Drift presentation is currently enabled. */
  private isActive = false;

  /**
   * Create the hidden home-pet composition and attach its state lifecycle.
   *
   * @param opts Standard view options and active-state callback for the owner.
   */
  constructor({ onActiveChanged, ...opts }: DreamDriftHomePetViewOpts) {
    // Create a centered, non-interactive container for the complete composition.
    super({
      width: 360,
      height: 340,
      centerOnOrigin: true,
      canHandleEvents: false,
      visible: false,
      ...opts,
    });

    // Retain the callback used to synchronize the owning presentation layer.
    this.onActiveChanged = onActiveChanged;

    // Dim the complete room behind the active sleeping-pet presentation.
    this.background = new View({
      superview: this,
      width: uiConfig.width,
      height: uiConfig.height,
      backgroundColor: '#222346',
      opacity: 0.5,
      zIndex: -1,
      canHandleEvents: false,
    });

    // Keep the full composition separate from the stable responsive scene anchor.
    this.container = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      canHandleEvents: false,
    });

    // Draw the themed bed behind the sleeping pet.
    this.bed = new ImageScaleView({
      superview: this.container,
      image: dreamDriftTheme().getHomePetAssets().bed,
      width: downscale(612),
      height: downscale(482),
      x: this.style.width / 2,
      y: 135,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Place the existing countdown chip immediately beneath the bed.
    this.timer = new Match2Timer({
      superview: this.container,
      x: this.style.width / 2,
      y: 190,
      centerOnOrigin: true,
      zIndex: 2,
      canHandleEvents: false,
    });
    this.timer.setRemainingTimeGetter((currentTime) =>
      dreamDriftGetInstanceRemainingTime(user(), currentTime),
    );
    this.timer.setOnUpdate((remainingTime) =>
      this.updateTimerPresentation(remainingTime),
    );

    // Reconcile immediately when the stored instance starts or reaches a terminal state.
    createEmitter(this, ({ user }) => user.dreamDrift?.instance).addListener(
      () => this.updateActiveState(),
    );

    // Keep the centered composition aligned with responsive screen-height changes.
    createEmitter(this, ({ ui }) => ui.screenSize).addListener(() =>
      this.updateLayout(),
    );

    // Resume or suspend timer work with the scene hierarchy lifecycle.
    this.on('ViewAdded', () => this.updateActiveState(true));
    this.on('ViewRemoved', () => this.timer.stopAutoUpdate());

    // Update the internal composition every frame for continuous floating motion.
    this.setTick(this.tick.bind(this));

    // Initialize the responsive position before the scene first enters the hierarchy.
    this.updateLayout();
  }

  /**
   * Load the active theme's home-pet artwork.
   *
   * @returns Promise resolved when the themed assets are ready for display.
   */
  public async preload(): Promise<void> {
    // Load the dedicated home-state art without requiring the progression map.
    await new AssetGroup(
      Object.values(dreamDriftTheme().getHomePetAssets()),
    ).load();
  }

  /** Float the complete home-pet composition through a ten-pixel vertical range. */
  public tick(): void {
    // Convert elapsed browser time into the same smooth motion used by the map pin.
    const floatOffset = Math.sin(performance.now() / 500) * 5;

    // Move the visuals while preserving the responsive outer scene anchor.
    this.container.updateOpts({ y: floatOffset });
  }

  /** Update the component position from the current responsive screen height. */
  private updateLayout(): void {
    // Center the composition over the normal living-room pet area.
    const { height } = StateObserver.getState().ui.screenSize;
    const x = uiConfig.width / 2;
    const y = height * 0.58;
    this.updateOpts({
      x,
      y,
    });

    // Offset the background from the centered component to fill the whole scene.
    this.background.updateOpts({
      width: uiConfig.width,
      height,
      x: this.style.width / 2 - x,
      y: this.style.height / 2 - y,
    });
  }

  /**
   * Reconcile the visible presentation from current state and server time.
   *
   * @param notifyOwner Whether the owner must reapply its visual pet mask.
   */
  private updateActiveState(notifyOwner = false): void {
    // Derive activity instead of persisting a second client-side lifecycle flag.
    const active = dreamDriftIsInstanceActive(user(), now());
    this.setActive(active, notifyOwner);
  }

  /**
   * Apply one active-state transition to the custom and regular pet views.
   *
   * @param active Whether the Dream Drift instance currently has time remaining.
   * @param notifyOwner Whether unchanged activity must be sent to the owner.
   */
  private setActive(active: boolean, notifyOwner = false): void {
    // Keep the sleeping pet species synchronized whenever the active view is requested.
    if (active) {
      // A persistent home screen must pick up the artwork of the newly started event.
      this.bed.setImage(dreamDriftTheme().getHomePetAssets().bed);
      this.updateSleepingPet();
    }

    // Repeated active updates only need to keep the visible timer running.
    if (active === this.isActive) {
      if (active && this.getApp()) this.timer.autoUpdate();
      if (notifyOwner) this.onActiveChanged(active);
      return;
    }

    // Store the new presentation state before notifying the owning scene.
    this.isActive = active;

    // Active instances show the custom composition and start its countdown.
    if (active) {
      this.show();
      if (this.getApp()) this.timer.autoUpdate();
      this.onActiveChanged(true);
      return;
    }

    // Ended instances stop the timer and return presentation ownership to the screen.
    this.timer.stopAutoUpdate();
    this.hide();
    this.onActiveChanged(false);
  }

  /** Create or refresh the sleeping UI pet for the player's current species. */
  private updateSleepingPet(): void {
    // Dream Drift instances require an existing pet species to present.
    const species = getPet().species;
    if (!species) return;

    // Keep the existing entity when it already represents the current species.
    if (this.sleepingPet && this.sleepingPetSpecies === species) return;

    // Remove a stale species before creating its replacement presentation.
    this.sleepingPet?.removeFromSuperview();

    // Create a separate sleeping entity so the real pet remains state-synchronized.
    this.sleepingPet = createUIPet('sleeping', species);
    this.sleepingPetSpecies = species;
    const sleepingPetView = this.sleepingPet.getView();
    sleepingPetView.updateOpts({ canHandleEvents: true });
    sleepingPetView.onClick = () => void dreamDriftOpen();
    this.sleepingPet.updateOpts({
      superview: this.container,
      x: this.style.width / 2,
      y: 60,
      scale: 0.8,
      zIndex: 1,
    });
  }

  /**
   * Update urgency styling and end the presentation when its timer reaches zero.
   *
   * @param remainingTime Server-authoritative Dream Meter duration in milliseconds.
   */
  private updateTimerPresentation(remainingTime: number): void {
    // Use a local urgent treatment during the final fifteen minutes.
    const isUrgent =
      remainingTime > 0 && remainingTime < duration({ minutes: 15 });
    this.timer.text.updateOpts({ color: isUrgent ? '#E95673' : '#BF8147' });

    // Positive time keeps the active presentation visible.
    if (remainingTime > 0) return;

    // Timer expiry restores the regular pet without waiting for a state write.
    this.setActive(false);
  }
}
